import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    linkedSignal,
    model,
    output,
    Signal,
    signal
} from '@angular/core';
import { DataAggregation } from 'src/app/pbir/dataModel';
import { debounced } from 'src/app/pbir/utils/debounced';
import { Predicate, PredicateType } from 'src/app/pbir/utils/Predicate';
import { VisualDataSource } from 'src/app/pbir/visuals/data';
import { VisualContext } from 'src/app/pbir/visuals/VisualContext';
import { VisualCustomField } from 'src/app/pbir/visuals/VisualCustomField';
import { VisualFieldTarget, VisualInvalidTarget } from 'src/app/pbir/visuals/VisualFieldTarget';
import { VisualFilter } from 'src/app/pbir/visuals/VisualFilter';
import { DataTypeMap } from 'src/app/shared/compatibility/filter.models';
import { SharedModule } from 'src/app/shared/shared.module';
import { VisualEditorFieldIconsComponent } from '../layout/FieldIcons.component';
import { AdvancedFilterComponent } from './AdvancedFilter.component';
import { BasicFilterComponent } from './BasicFilter.component';
import { DynamicFilterComponent } from './DynamicFilter.component';
import { RelativeDateOrTimeFilterComponent } from './RelativeDateOrTime.component';
import { TopNFilterComponent } from './TopNFilter.component';
import { AppContext } from '../../../../app/context';
import { DataType } from '../../../../pbir/dataModel/DataType';

@Component({
  selector: 'filter-editor',
  templateUrl: './FilterEditor.component.html',
  styles: [`
    :host .visual-editor-box__input-horizontal {
      grid-template-columns: minmax(60px, max-content) 1fr;
      column-gap: 6px !important;
      row-gap: 6px !important;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SharedModule,
    BasicFilterComponent, 
    AdvancedFilterComponent,
    TopNFilterComponent,
    RelativeDateOrTimeFilterComponent,
    DynamicFilterComponent,
    VisualEditorFieldIconsComponent
  ],
})
export class FilterEditorComponent {
  readonly DataAggregation = DataAggregation;
  readonly PredicateType = PredicateType;
  
  readonly destroyRef = inject(DestroyRef);
  readonly appContext = inject(AppContext);
  
  readonly context = input<VisualContext>();
  readonly dataSource = input<VisualDataSource>();
  readonly customFields = input<ReadonlyArray<VisualCustomField>>();

  readonly requireAggregation = computed(() =>
    !!Predicate.aggregationsOnly(this.predicateType()) ||
    !!this.predicate()?.target.mustHaveAggregation);


  readonly groups = input<ReadonlyArray<FilterEditor.Group>>([]);

  readonly group = computed(() => {
    let groups = this.groups();
    let target = this.filter()?.target();
    if (!groups || !target)
      return null;

    let groupName = target.itemGroupName.toLowerCase();
    return groups.find(g => g.title.toLowerCase() == groupName);
  });
  
  readonly items = computed(() => {
    let [group] = [this.group()];
    if (!group?.items?.length)
      return [];

    let items = group.items;

    if (this.requireAggregation())
      items = items.filter(x => x.target().canAssignAggregation || x.target().isAggregated);

    return items;
  });

  readonly item = computed(() => {
    let group = this.group();
    let target = this.filter()?.target();
    if (!group || !target)
      return null;

    return group?.findItemByTarget(target);
  });

  readonly aggregations = computed(() => {
    let target = this.item()?.target();
    if (!target || !target.canAssignAggregation)
      return [];

    let aggregations = [];

    let canUseDoNotSummarize = !this.requireAggregation() || this.appContext.user().isSuperAdmin;
    if (canUseDoNotSummarize) {
      if (![PredicateType.TopN].includes(this.predicateType())) {
        aggregations.push({ value: null, title: 'Do not summarize' });
      }
    }

    aggregations.push(
      ...DataAggregation.list
        .filter(f => target.unaggregatedDataType.canAggregateWith(f))
        .map(x => ({
          value: x,
          title: x.title,
        }))
    );

    return aggregations;
  });

  readonly itemAggregation = computed(() => {
    let target = this.filter()?.target();
    if (!target)
      return null;

    return this.aggregations().find(a => a.value == target.aggregation);
  });

  readonly types = computed(() => {
    return this.getTypes(this.item()?.target());
  });

  private getTypes(target: VisualFieldTarget) {
    if (!target)
      return [];

    let dataType = target.unaggregatedDataType;
    if (!dataType)
      return [];

    let aggregated = target.isAggregated;
    let support = DataTypeMap[dataType.value].support;
    let types: FilterEditor.Type[] = [];
    
    if (support.advanced)
      types.push(FilterEditor.Type.Advanced);

    if (support.basic && !target.mustHaveAggregation)
      types.push(FilterEditor.Type.Basic);

    if (support.relativeDateOrTime) {
      if (dataType.isDateOrDateTime)
        types.push(FilterEditor.Type.RelativeDate);

      if (dataType.isTimeOnly)
        types.push(FilterEditor.Type.RelativeTime);
    }

    if (support.topN)
      types.push(FilterEditor.Type.TopN);

    if (support.dynamic)
      types.push(FilterEditor.Type.Dynamic);

    if (aggregated)
      types = types.filter(t => VisualFilter.Predicate.allowsAggregatedTarget(t.type));

    return types;
  }

  readonly predicateType = computed(() => this.predicate()?.type);

  readonly type = computed(() => {
    let predicateType = this.predicateType();
    return this.types()?.find(t => t.type === predicateType);
  });

  readonly isBasic = computed(() => this.type()?.type === PredicateType.Basic);
  readonly isAdvanced = computed(() => this.type()?.type === PredicateType.Advanced);
  readonly isDynamic = computed(() => this.type()?.type === PredicateType.Dynamic);
  readonly isRelativeDate = computed(() => this.type()?.type === PredicateType.RelativeDate);
  readonly isRelativeTime = computed(() => this.type()?.type === PredicateType.RelativeTime);
  readonly isTopN = computed(() => this.type()?.type === PredicateType.TopN);

  readonly filter = input<VisualFilter>();
  readonly filterChange = output<VisualFilter>();

  setGroup(group: FilterEditor.Group) {
    this.setItem(group.items.length ? group.items[0] : null);
  }

  setItem(item: FilterEditor.Item) {
    let target = item?.target();
    let aggregation = this.itemAggregation()?.value;
   
    if (target) {
      if (this.requireAggregation()) {
        if (!target.isAggregated && !target.canAssignAggregation)
          target = null;
        else {
          if (!aggregation || !target.unaggregatedDataType.canAggregateWith(aggregation))
            aggregation = target.unaggregatedDataType.defaultAggregation;
        }
      }

      if (target &&
        target.canAssignAggregation &&
        target.unaggregatedDataType.canAggregateWith(aggregation)) {
        target = target.withAggregation(aggregation);
      }
    }

    this.setPredicate({
      ...this.filter().predicate(),
      target
    }, true);
  }

  setItemAggregation(aggregation: DataAggregation | null) {
    this.setPredicate({
      ...this.filter().predicate(),
      target: this.item()?.target().withAggregation(aggregation),
    }, true);
  }

  setType(type: FilterEditor.Type) {
    this.setPredicate({
      ...this.filter().predicate(),
      ...VisualFilter.Predicate.fromTarget(this.filter().predicate().target, <any>type?.type)
    }, true);
  }


  readonly predicate = linkedSignal(() => {
    return this.filter()?.predicate();
  });

  setPredicate(predicate: Partial<VisualFilter.Predicate>, immediate: boolean = false) {
    let current = this.predicate();

    if (predicate?.target) {
      let types = this.getTypes(predicate.target);
      if (types.length && !types.some(x => x.type == predicate.type)) {
        predicate = {
          ...predicate,
          ...VisualFilter.Predicate.fromTarget(predicate.target, <any>types[0].type)
        };
      }
    }

    if (current?.target && (
     !predicate?.target?.dataType.isAssignableTo(current?.target?.dataType, true) ||
     !current?.target?.dataType.isAssignableTo(predicate?.target?.dataType, true))) {
      predicate = {
        ...predicate,
        ...VisualFilter.Predicate.fromTarget(predicate.target, predicate.type)
      };
    }
    
    let target = predicate?.target;
    if (target) {
      if (Predicate.aggregationsOnly(predicate?.type)) {
        if (!target.isAggregated) {
          if (target.canAssignAggregation) {
            target = target.withAggregation(target.unaggregatedDataType.defaultAggregation);
          }
          else {
            target = null;
          }

          predicate = { ...predicate, target };
        }
      }
    }

    this.predicate.set(<any>predicate);

    if (immediate) {
      this.updateFilterPredicate();
    }
    else {
      this.updateFilterPredicateDebounced();
    }
  }

  updateFilterPredicate() {
    this.updateFilterPredicateDebounced.cancel();
    this.filter().predicate.set(this.predicate());
    this.filterChange.emit(this.filter());
  }

  private updateFilterPredicateDebounced = debounced(
    () => this.updateFilterPredicate(), 
    100,
    this.destroyRef);


  readonly canSelectAggregation = computed(() => {
    let predicate = this.predicate();

    if (!this.aggregations().length || (this.invalidFieldDetails() && !this.invalidFieldDetails()?.aggregation))
      return false;

    if (!predicate || !predicate.target.canAssignAggregation)
      return false;

    return this.canUseAggregationWith(predicate);
  });

  private canUseAggregationWith(predicate: Partial<VisualFilter.Predicate>) {
    if (!predicate || !predicate.target.canAssignAggregation)
      return false;

    return VisualFilter.Predicate.allowsAggregatedTarget(predicate.type);
  }


  readonly viewOnly = input(false);

  readonly showFieldSelector = computed(() => {
    let target = this.filter()?.target();
    if (!target)
      return true;

    if (target.isValid)
      return true;

    if (!!target.customField)
      return true;

    // Cannot show field selector for invalid fields
    return false;
  });

  readonly invalidFieldDetails = computed(() => {
    if (this.showFieldSelector())
      return null;

    let target = this.filter()?.target();
    if (target instanceof VisualInvalidTarget) {
      let dto = target.schemaFieldTargetDto;

      return {
        group: dto.group,
        field: dto.childField ? `${dto.field} - ${dto.childField}` : dto.field,
        aggregation: DataAggregation.from(dto.aggregation)?.title ?? 'Do not summarize'
      };
    }

    return null;
  });


  toggleItem(item: FilterEditor.Item, e: Event) {
    e.stopPropagation();

    item.collapsed.update(x => !x);
  }

  private findItemByTarget(target: VisualFieldTarget) {
    for (let g of this.groups()) {
      let i = g.findItemByTarget(target);
      if (i)
        return i;
    }

    return null;
  }

  dropData() {
    return this.filter();
  }
}

export namespace FilterEditor {
  let nextGroupId = 1;

  export class Group {
    readonly id = nextGroupId++;
    
    public readonly items: Item[] = [];

    constructor(public readonly title: string) { }

    findItemByTarget(target: VisualFieldTarget) {
      return this.items.find(i => i.target().sameSource(target));
    }
  }


  let nextItemId = 1;

  export class Item {
    readonly id = nextItemId++;

    constructor(
      public readonly group: Group,
      public readonly target: Signal<VisualFieldTarget>,
      public readonly shortTitle: Signal<string>,
      public readonly longTitle: Signal<string>,
      public readonly icon: Signal<string>, 
      public readonly hasChildren: boolean, 
      public readonly parent?: Item) { }

    readonly collapsed = signal(true);
  }

  export interface Type {
    readonly type: PredicateType;
    readonly title: string;
  }

  export namespace Type {
    export const Basic: Type = {
      type: PredicateType.Basic,
      title: 'Basic'
    };

    export const Advanced: Type = {
      type: PredicateType.Advanced,
      title: 'Advanced'
    };

    export const RelativeDate: Type = {
      type: PredicateType.RelativeDate,
      title: 'Relative Date'
    };

    export const RelativeTime: Type = {
      type: PredicateType.RelativeTime,
      title: 'Relative Time'
    };

    export const TopN: Type = {
      type: PredicateType.TopN,
      title: 'Top N'
    };

    export const Dynamic: Type = {
      type: PredicateType.Dynamic,
      title: 'Dynamic'
    };
  }
}
